import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { ConfirmDialog } from "src/ui/containers/dashboard/devices/components/shared";
import { DangerOutlinedButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { getErrorMessage } from "src/ui/shared/toasts/useQueryErrorToast";

import { useDeleteFileMutation } from "@dashboard/files/api";
import apiClient from "@dashboard/files/api/apiClient";
import { Box, DialogContentText, Typography } from "@mui/material";
import { CollapsibleDetails, CollapsibleRow, CollapsibleSummary } from "@shared/table/CollapsibleRow";
import { flexRender, Row } from "@tanstack/react-table";

import { State } from "./AdminFilesPage";
import { useStyles } from "./AdminFilesPage.style";

type TableItemProps = {
    setState: (value: React.SetStateAction<State>) => void;
    row: Row<API.File>;
};

const TableItem: React.FC<TableItemProps> = ({ setState, row }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const [deleteFileId, setDeleteFileId] = React.useState<string | null>(null);
    const [fetchingFileId, setFetchingFileId] = React.useState<string | null>(null);

    const fileItem = row.original;

    const [deleteFile] = useDeleteFileMutation();

    const onDelete = async () => {
        if (deleteFileId) {
            const result = await deleteFile(deleteFileId);
            if ("error" in result) {
                showErrorToast(getErrorMessage(result.error));
            } else {
                setState((prev) => ({
                    ...prev,
                    count: prev.count - 1,
                    data: prev.data.filter((item) => item.id !== deleteFileId),
                }));
                showSuccessToast(t("common:section.uploadField.toast.fileDeletedSuccess"));
            }
        }
    };

    const onDownload = async (item: API.File) => {
        try {
            setFetchingFileId(item.id);
            await apiClient.invokeFileDownload(item.id, item.filename);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setFetchingFileId(null);
        }
    };

    return (
        <CollapsibleRow>
            <CollapsibleSummary row={row}>
                {row
                    .getVisibleCells()
                    .map((cell) => flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }))}
                <MinimalTableCell align="right">
                    <div className={classes.actionCell}>
                        <SecondaryButton
                            onClick={() => onDownload(fileItem)}
                            loading={fileItem.id === fetchingFileId}
                            disabled={fileItem.id === fetchingFileId}
                        >
                            <i className="fa-solid fa-download" />
                        </SecondaryButton>
                        <DangerOutlinedButton onClick={() => setDeleteFileId(fileItem.id)}>
                            <i className="fa-solid fa-trash" />
                        </DangerOutlinedButton>
                    </div>
                </MinimalTableCell>
            </CollapsibleSummary>
            <CollapsibleDetails>
                <Box className={classes.flex}>
                    <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.fileId")}: </Typography>
                    <Typography>{fileItem.id}</Typography>
                </Box>
                {fileItem?.metadata?.file?.apk ? (
                    <>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.package")}: </Typography>
                            <Typography>{fileItem.metadata.file.apk.manifest?.package}</Typography>
                        </Box>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.versionCode")}:</Typography>
                            <Typography>{fileItem.metadata.file.apk.manifest?.versioncode}</Typography>
                        </Box>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.versionName")}:</Typography>
                            <Typography>{fileItem.metadata.file.apk.manifest?.versionname}</Typography>
                        </Box>
                    </>
                ) : null}
                {fileItem?.metadata?.version ? (
                    <>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.version")}: </Typography>
                            <Typography>{fileItem.metadata.version}</Typography>
                        </Box>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.codename")}: </Typography>
                            <Typography>{fileItem.metadata.codename}</Typography>
                        </Box>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.productId")}: </Typography>
                            <Typography>{fileItem.metadata.productId}</Typography>
                        </Box>
                        <Box className={classes.flex}>
                            <Typography className={classes.boldLabel}>{t("pages:adminFilesPage.productName")}: </Typography>
                            <Typography>{fileItem.metadata.productName}</Typography>
                        </Box>
                    </>
                ) : null}
                {fileItem?.metadata?.otaMetadata ? (
                    <>
                        <Box className={classes.flex}>
                            <Typography style={{ fontWeight: 600 }}>{t("pages:adminFilesPage.otaUpdate")}</Typography>
                        </Box>
                    </>
                ) : null}
            </CollapsibleDetails>
            <ConfirmDialog
                title={t("modals:deleteFile.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={!!deleteFileId}
                onConfirm={onDelete}
                onClose={() => setDeleteFileId(null)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteFile.description"
                        values={{ filename: fileItem.filename }}
                        components={{ bold: <span style={{ fontWeight: "bold" }} /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </CollapsibleRow>
    );
};

export default TableItem;
